import React, { useMemo, useState, useCallback } from 'react'
import { Table2, Loader2, Download, ChevronRight, ChevronDown } from 'lucide-react'
import { ThemePreset } from '../../theme/presets'

interface AnalyticsRow {
  [key: string]: unknown
  barras?: number | string
  peso?: number | string
  valor?: number | string
  preco_kilo?: number | string
}

export interface DrilldownConfig {
  title: string
  columns: string[]
  levelLabels: string[]
  levelBadges: (string | null)[]
}

interface AnalyticsTableProps {
  data: AnalyticsRow[]
  totalRows?: number
  loading: boolean
  palette: ThemePreset
  drilldown?: DrilldownConfig
  onExport?: () => void
  onLoadMore?: () => void
  hasMore?: boolean
  loadingMore?: boolean
}

interface TreeNode {
  key: string
  level: number
  label: string
  barras: number
  peso: number
  valor: number
  preco_kilo: number
  children: TreeNode[]
}

const num = (v: number | string | undefined): number => {
  const n = Number(v ?? 0)
  return Number.isNaN(n) ? 0 : n
}

const fmtMoney = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)

const fmtNumber = (v: number, d = 2): string =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(v)

function buildTree(rows: AnalyticsRow[], columns: string[]): TreeNode[] {
  if (!columns.length || !rows.length) return []

  const depth = columns.length

  // indexMap[depth][key] = row or map
  const indexMap: Array<Map<string, unknown>> = []
  for (let d = 0; d < depth; d++) {
    indexMap.push(new Map())
  }

  // Group rows by the first column
  const root = new Map<string, AnalyticsRow[]>()
  for (const row of rows) {
    const k = String(row[columns[0]] ?? '(vazio)')
    if (!root.has(k)) root.set(k, [])
    root.get(k)!.push(row)
  }

  // Build tree recursively
  function buildLevel(rows: AnalyticsRow[], colIdx: number, parentKey: string): TreeNode[] {
    if (colIdx >= depth) {
      // Leaf level - individual rows
      return rows.map((r) => ({
        key: `${parentKey}|${String(r[columns[depth - 1]] ?? '')}|${Math.random().toString(36).slice(2, 8)}`,
        level: depth + 1,
        label: String(r[columns[depth - 1]] ?? '(vazio)'),
        barras: num(r.barras),
        peso: num(r.peso),
        valor: num(r.valor),
        preco_kilo: num(r.preco_kilo),
        children: [],
      }))
    }

    const groups = new Map<string, AnalyticsRow[]>()
    for (const r of rows) {
      const k = String(r[columns[colIdx]] ?? '(vazio)')
      if (!groups.has(k)) groups.set(k, [])
      groups.get(k)!.push(r)
    }

    const nodes: TreeNode[] = []
    for (const [key, groupRows] of groups) {
      const children = buildLevel(groupRows, colIdx + 1, `${parentKey}|${key}`)
      nodes.push({
        key: `${parentKey}|${key}`,
        level: colIdx + 1,
        label: key,
        barras: children.reduce((a, c) => a + c.barras, 0),
        peso: children.reduce((a, c) => a + c.peso, 0),
        valor: children.reduce((a, c) => a + c.valor, 0),
        preco_kilo: 0,
        children: children.length > 0 && children[0].level > colIdx + 1 ? children : [],
      })
    }
    return nodes.sort((a, b) => b.valor - a.valor)
  }

  const tree: TreeNode[] = []
  for (const [key, groupRows] of root) {
    const children = buildLevel(groupRows, 1, key)
    tree.push({
      key,
      level: 1,
      label: key,
      barras: children.reduce((a, c) => a + c.barras, 0),
      peso: children.reduce((a, c) => a + c.peso, 0),
      valor: children.reduce((a, c) => a + c.valor, 0),
      preco_kilo: 0,
      children,
    })
  }

  return tree.sort((a, b) => b.valor - a.valor)
}

interface FlatRow {
  treeNode: TreeNode
  depth: number
  hasChildren: boolean
}

const DEFAULT_DRILLDOWN: DrilldownConfig = {
  title: 'Drilldown Analítico',
  columns: ['codigo_alu', 'produto', 'cliente'],
  levelLabels: ['ALU', 'Produto', 'Cliente'],
  levelBadges: ['ALU', null, null],
}

export const AnalyticsTable: React.FC<AnalyticsTableProps> = ({
  data,
  totalRows,
  loading,
  palette,
  drilldown = DEFAULT_DRILLDOWN,
  onExport,
  onLoadMore,
  hasMore,
  loadingMore,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const tree = useMemo(() => buildTree(data, drilldown.columns), [data, drilldown.columns])

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const flatRows = useMemo(() => {
    const result: FlatRow[] = []
    function walk(nodes: TreeNode[], depth: number) {
      for (const node of nodes) {
        const hasChildren = node.children.length > 0
        result.push({ treeNode: node, depth, hasChildren })
        if (hasChildren && expanded.has(node.key)) {
          walk(node.children, depth + 1)
        }
      }
    }
    walk(tree, 0)
    return result
  }, [tree, expanded])

  const headerLabel = drilldown.levelLabels.length
    ? drilldown.levelLabels.join(' / ')
    : drilldown.columns.join(' / ')

  const levelBadge = (level: number): string | null => {
    if (!drilldown.levelBadges || level > drilldown.levelBadges.length) return null
    return drilldown.levelBadges[level - 1] ?? null
  }

  const cells = (node: TreeNode, depth: number, hasChildren: boolean) => {
    const indent = depth * 28
    const isMetric = node.level <= drilldown.columns.length
    const preco = isMetric
      ? (node.peso > 0 ? node.valor / node.peso : 0)
      : node.preco_kilo
    const badge = levelBadge(node.level)

    return (
      <>
        <div className="flex items-center px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 truncate" style={{ flex: '2 1 0%', minWidth: 180, paddingLeft: indent + 12 }}>
          {hasChildren ? (
            <button
              onClick={() => toggle(node.key)}
              className="mr-1.5 flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              {expanded.has(node.key) ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <span className="w-5 mr-0.5 flex-shrink-0" />
          )}
          <span className={`truncate ${node.level === 1 ? 'font-semibold' : ''}`} title={node.label}>
            {badge && (
              <span className="text-xs px-1.5 py-0.5 rounded mr-1.5 font-medium" style={{ backgroundColor: `${palette.primary}15`, color: palette.primary }}>
                {badge}
              </span>
            )}
            {node.label}
          </span>
        </div>
        <div className="px-3 py-2.5 text-sm text-right tabular-nums text-slate-700 dark:text-slate-300 truncate" style={{ flex: '0 0 90px' }}>
          {fmtNumber(node.barras, 0)}
        </div>
        <div className="px-3 py-2.5 text-sm text-right tabular-nums text-slate-700 dark:text-slate-300 truncate" style={{ flex: '0 0 110px' }}>
          {fmtNumber(node.peso, 2)}
        </div>
        <div className="px-3 py-2.5 text-sm text-right tabular-nums font-medium text-slate-800 dark:text-white truncate" style={{ flex: '0 0 130px' }}>
          {fmtMoney(node.valor)}
        </div>
        <div className="px-3 py-2.5 text-sm text-right tabular-nums text-slate-500 dark:text-slate-400 truncate" style={{ flex: '0 0 100px' }}>
          {fmtMoney(preco)}
        </div>
      </>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
        <Table2 className="w-5 h-5" style={{ color: palette.primary }} />
        <h3 className="text-base font-semibold text-slate-800 dark:text-white">
          {drilldown.title}
        </h3>
        <div className="flex items-center gap-2 ml-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-400">
            {tree.length} {drilldown.levelLabels[0] ?? 'grupos'}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-400">
            {flatRows.length} linhas visíveis
          </span>
        </div>
        {onExport && (
          <button
            onClick={onExport}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Exportar XLSX
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[400px]">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : !data.length ? (
        <div className="flex items-center justify-center h-[400px] text-slate-400 text-sm">
          Selecione um período e clique em Aplicar para carregar os dados.
        </div>
      ) : (
        <>
          <div className="overflow-hidden border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex">
              <div className="px-3 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-left" style={{ flex: '2 1 0%', minWidth: 180 }}>
                {headerLabel}
              </div>
              <div className="px-3 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right" style={{ flex: '0 0 90px' }}>
                Barras
              </div>
              <div className="px-3 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right" style={{ flex: '0 0 110px' }}>
                Peso
              </div>
              <div className="px-3 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right" style={{ flex: '0 0 130px' }}>
                Valor
              </div>
              <div className="px-3 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right" style={{ flex: '0 0 100px' }}>
                Preço/kg
              </div>
            </div>
          </div>

          <div className="overflow-auto scrollbar-standard" style={{ maxHeight: '550px' }}>
            {flatRows.map(({ treeNode: node, depth, hasChildren }) => {
              const bgClass = node.level === 1
                ? 'bg-white dark:bg-slate-900'
                : node.level === 2
                  ? 'bg-slate-50/40 dark:bg-slate-800/20'
                  : 'bg-slate-50/60 dark:bg-slate-800/10'
              const hoverClass = hasChildren ? 'cursor-pointer hover:bg-blue-50/50 dark:hover:bg-blue-900/10' : ''

              return (
                <div
                  key={node.key}
                  className={`flex border-b border-slate-50 dark:border-slate-800/30 transition-colors ${bgClass} ${hoverClass}`}
                  onClick={hasChildren ? () => toggle(node.key) : undefined}
                >
                  {cells(node, depth, hasChildren)}
                </div>
              )
            })}
          </div>

          {onLoadMore && hasMore && (
            <div className="flex items-center justify-center p-4 border-t border-slate-100 dark:border-slate-700">
              <button
                onClick={onLoadMore}
                disabled={loadingMore}
                className="px-6 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: palette.primary }}
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Carregando...
                  </>
                ) : (
                  `Carregar mais (${totalRows} total)`
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
